import React from "react";
import { useGetPostsQuery } from "@/features/api/postApi";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const BlogList = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPostsQuery();

  if (isLoading) {
    return (
      <div className="px-5 sm:px-6 md:px-8 lg:px-10 xl:px-20 max-w-screen-xl mx-auto mt-5">
        <h1 className="text-2xl font-semibold mt-5 mb-4">Blog</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden rounded-lg shadow">
              <Skeleton className="w-full h-40 rounded-t-lg" />
              <CardContent className="px-5 py-4 space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <div className="flex justify-between mt-3">
                  <Skeleton className="h-6 w-1/4" />
                  <Skeleton className="h-6 w-1/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-10 text-red-500">
        ❌ Failed to load blog posts.
      </div>
    );
  }

  const publishedPosts = data?.posts?.filter((post) => post.status === "published") || [];

  if (publishedPosts.length === 0) {
    return (
      <div className="text-center mt-10 text-gray-600">
        📭 No published blog posts found.
      </div>
    );
  }

  return (
    <div className="px-5 sm:px-6 md:px-8 lg:px-10 xl:px-20 max-w-screen-xl mx-auto">
      <h1 className="text-2xl font-semibold mt-5 mb-4">Blog</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {publishedPosts.map((post) => {
          // Consistent featured image extraction logic like PostView
          const featuredImageUrl =
            typeof post.featuredImage === "string"
              ? post.featuredImage
              : post.featuredImage?.url || "https://via.placeholder.com/600x200?text=No+Image";

          const featuredImageAlt =
            typeof post.featuredImage === "object"
              ? post.featuredImage?.altText || post.title
              : post.title;

          return (
            <Card
              key={post.slug}
              className="overflow-hidden rounded-lg bg-white shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer"
              onClick={() => navigate(`/blog/${post.slug}`)}
            >
              <div className="relative">
                <img
                  src={featuredImageUrl}
                  alt={featuredImageAlt}
                  className="w-full h-40 object-cover rounded-t-lg"
                  onError={(e) => {
                    e.currentTarget.src = "https://via.placeholder.com/600x200?text=No+Image";
                  }}
                />
              </div>
              <CardContent className="px-5 py-4 space-y-3">
                <h2 className="hover:underline font-bold text-lg truncate">{post.title}</h2>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {new Date(post.updatedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <Badge className="bg-blue-500 text-white px-2 py-1 text-xs rounded-full">
                    {post.category || "General"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default BlogList;
